import { generateClient } from 'aws-amplify/data';
import { getCurrentUser } from 'aws-amplify/auth';

export const SUBSCRIPTION_TIERS = {
  STARTER: {
    id: 'STARTER',
    name: 'Starter',
    maxNewslettersPerMonth: 10,
    maxClients: 50,
    priceMonthly: 29,
    stripePriceId: import.meta.env?.VITE_STRIPE_PRICE_STARTER || 'price_starter',
    features: ['10 newsletters/month', '50 clients', 'Basic templates', 'Email support'],
  },
  PROFESSIONAL: {
    id: 'PROFESSIONAL',
    name: 'Professional',
    maxNewslettersPerMonth: 50,
    maxClients: 250,
    priceMonthly: 79,
    stripePriceId: import.meta.env?.VITE_STRIPE_PRICE_PROFESSIONAL || 'price_professional',
    features: ['50 newsletters/month', '250 clients', 'Custom templates', 'AI writing style', 'Priority support'],
  },
  ENTERPRISE: {
    id: 'ENTERPRISE',
    name: 'Enterprise',
    maxNewslettersPerMonth: 500,
    maxClients: 2500,
    priceMonthly: 199,
    stripePriceId: import.meta.env?.VITE_STRIPE_PRICE_ENTERPRISE || 'price_enterprise',
    features: ['500 newsletters/month', 'Unlimited clients', 'Compliance audit trail', 'Dedicated support'],
  },
};

const STRIPE_CHECKOUT_URL = import.meta.env?.VITE_STRIPE_CHECKOUT_LAMBDA_URL || '';

class BillingService {
  constructor() {
    try {
      this.client = generateClient();
    } catch {
      this.client = null;
    }
  }

  async getCurrentUserId() {
    try {
      const user = await getCurrentUser();
      return user.userId;
    } catch {
      return null;
    }
  }

  async getSubscriptionStatus() {
    try {
      const userId = await this.getCurrentUserId();
      if (!userId) return { isActive: false, tier: null, error: 'Not authenticated' };

      const { data } = await this.client.models.UserSubscription.list({
        filter: { userId: { eq: userId } },
      });

      if (!data || data.length === 0) {
        return { isActive: false, tier: null, newslettersUsedThisMonth: 0 };
      }

      const subscription = data[0];

      // Free trial awareness
      const isFreeTrial = Boolean(subscription.isFreeTrial);
      const trialExpired =
        isFreeTrial &&
        subscription.trialEndDate &&
        new Date(subscription.trialEndDate) <= new Date();

      const isExpired =
        trialExpired ||
        subscription.status === 'EXPIRED' ||
        (subscription.currentPeriodEnd &&
          new Date(subscription.currentPeriodEnd) < new Date());

      const isActive = subscription.status === 'ACTIVE' && !isExpired;

      return {
        isActive,
        tier: isActive ? subscription.tier : null,
        subscriptionId: subscription.id,
        stripeSubscriptionId: subscription.stripeSubscriptionId,
        currentPeriodEnd: subscription.currentPeriodEnd,
        newslettersUsedThisMonth: subscription.newslettersUsedThisMonth || 0,
        status: subscription.status,
        // Free trial fields
        isFreeTrial: subscription.isFreeTrial || false,
        trialEndDate: subscription.trialEndDate || null,
      };
    } catch (error) {
      console.error('[BillingService] Error getting subscription status:', error);
      return { isActive: false, tier: null, error: error.message };
    }
  }

  async canGenerateNewsletter() {
    const status = await this.getSubscriptionStatus();

    if (!status.isActive) {
      return {
        allowed: false,
        reason: 'Active subscription required to generate newsletters. Please subscribe to continue.',
        upgradeUrl: '/settings/billing',
      };
    }

    const tierConfig = SUBSCRIPTION_TIERS[status.tier];
    if (!tierConfig) {
      return { allowed: false, reason: 'Invalid subscription tier.' };
    }

    if (status.newslettersUsedThisMonth >= tierConfig.maxNewslettersPerMonth) {
      return {
        allowed: false,
        reason: `Monthly newsletter limit reached (${tierConfig.maxNewslettersPerMonth}). Upgrade your plan for more.`,
        currentUsage: status.newslettersUsedThisMonth,
        limit: tierConfig.maxNewslettersPerMonth,
        upgradeUrl: '/settings/billing',
      };
    }

    return {
      allowed: true,
      remainingNewsletters: tierConfig.maxNewslettersPerMonth - status.newslettersUsedThisMonth,
      tier: status.tier,
    };
  }

  async createCheckoutSession(tier) {
    if (!SUBSCRIPTION_TIERS[tier]) {
      throw new Error(`Invalid subscription tier: ${tier}. Must be one of: ${Object.keys(SUBSCRIPTION_TIERS).join(', ')}`);
    }

    const userId = await this.getCurrentUserId();
    if (!userId) throw new Error('User must be authenticated to subscribe');

    const tierConfig = SUBSCRIPTION_TIERS[tier];

    const response = await fetch(STRIPE_CHECKOUT_URL, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        action: 'create-checkout-session',
        priceId: tierConfig.stripePriceId,
        userId,
        tier,
        successUrl: `${window.location.origin}/settings/billing?success=true`,
        cancelUrl: `${window.location.origin}/settings/billing?cancelled=true`,
      }),
    });

    if (!response.ok) {
      const error = await response.json().catch(() => ({}));
      throw new Error(error.error || 'Failed to create checkout session');
    }

    return response.json();
  }

  async handleSubscriptionUpdate(event) {
    const { type, userId, tier, stripeSubscriptionId, currentPeriodEnd } = event;

    if (type === 'checkout.session.completed') {
      const { data } = await this.client.models.UserSubscription.create({
        userId,
        tier,
        status: 'ACTIVE',
        stripeSubscriptionId,
        currentPeriodEnd,
        newslettersUsedThisMonth: 0,
        createdAt: new Date().toISOString(),
      });
      return { status: 'ACTIVE', subscription: data };
    }

    if (type === 'customer.subscription.deleted') {
      const { data: subscriptions } = await this.client.models.UserSubscription.list({
        filter: { stripeSubscriptionId: { eq: stripeSubscriptionId } },
      });

      if (subscriptions && subscriptions.length > 0) {
        const { data } = await this.client.models.UserSubscription.update({
          id: subscriptions[0].id,
          status: 'CANCELLED',
          cancelledAt: new Date().toISOString(),
        });
        return { status: 'CANCELLED', subscription: data };
      }
    }

    return { status: 'UNKNOWN' };
  }

  async incrementNewsletterUsage() {
    const status = await this.getSubscriptionStatus();
    if (!status.subscriptionId) return;

    try {
      await this.client.models.UserSubscription.update({
        id: status.subscriptionId,
        newslettersUsedThisMonth: (status.newslettersUsedThisMonth || 0) + 1,
      });
    } catch (error) {
      console.error('[BillingService] Error incrementing newsletter usage:', error);
    }
  }

  /**
   * Open the Stripe Customer Portal for the given customer.
   * POSTs to the portal Lambda endpoint and returns the session URL.
   *
   * @param {string} stripeCustomerId - Stripe customer ID (cus_...)
   * @returns {Promise<Object>} Object containing { portalUrl }
   */
  async createPortalSession(stripeCustomerId) {
    const portalUrl =
      import.meta.env?.VITE_STRIPE_PORTAL_URL ||
      (STRIPE_CHECKOUT_URL ? `${STRIPE_CHECKOUT_URL}/portal` : '');

    const response = await fetch(portalUrl, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ customerId: stripeCustomerId }),
    });

    if (!response.ok) {
      const error = await response.json().catch(() => ({}));
      throw new Error(error.error || 'Failed to create portal session');
    }

    return response.json();
  }

  /**
   * Upgrade or downgrade the current subscription tier with proration.
   *
   * @param {string} newTier - One of STARTER | PROFESSIONAL | ENTERPRISE
   * @returns {Promise<Object>} Updated subscription object from Stripe
   */
  async changeSubscriptionTier(newTier) {
    if (!SUBSCRIPTION_TIERS[newTier]) {
      throw new Error(
        `Invalid subscription tier: ${newTier}. Must be one of: ${Object.keys(SUBSCRIPTION_TIERS).join(', ')}`
      );
    }

    const status = await this.getSubscriptionStatus();
    if (!status.isActive || !status.stripeSubscriptionId) {
      throw new Error('No active subscription found. Cannot change tier.');
    }

    const newTierConfig = SUBSCRIPTION_TIERS[newTier];

    const response = await fetch(STRIPE_CHECKOUT_URL, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        action: 'update_subscription',
        subscriptionId: status.stripeSubscriptionId,
        newPriceId: newTierConfig.stripePriceId,
        proration: true,
      }),
    });

    if (!response.ok) {
      const error = await response.json().catch(() => ({}));
      throw new Error(error.error || 'Failed to update subscription');
    }

    const result = await response.json();
    return result.subscription;
  }

  /**
   * Start a 14-day free trial for the authenticated user.
   * Creates a Subscription record with isFreeTrial=true, tier=STARTER, status=ACTIVE.
   *
   * @returns {Promise<Object>} Created subscription record
   * @throws {Error} If user is not authenticated
   */
  async startFreeTrial() {
    const userId = await this.getCurrentUserId();
    if (!userId) throw new Error('User must be authenticated to start a free trial');

    const trialEndDate = new Date(Date.now() + 14 * 24 * 60 * 60 * 1000).toISOString();

    const { data } = await this.client.models.UserSubscription.create({
      userId,
      tier: 'PROFESSIONAL',
      status: 'ACTIVE',
      isFreeTrial: true,
      trialEndDate,
      newslettersUsedThisMonth: 0,
      createdAt: new Date().toISOString(),
    });

    return data;
  }
}

const billingServiceInstance = new BillingService();
export default billingServiceInstance;
export { BillingService };
